import { Alert, Button, Form, Input, message, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Container, Content, FormCard, Welcome } from './styles';
import React, { useEffect, useState } from 'react';
import { resetPassword, getUserInfo } from '../../utils/httpRequest';
import { useNavigate } from 'react-router';
import { useAppDispatch } from '../../store';
import { useSelector } from 'react-redux';
import {
  setUser,
  selectHasProfile,
  selectUserId,
  selectEmail,
} from '../../slices/profile';
import { SVGLogoDark } from '../../images';
import { useTranslation } from 'react-i18next';
import {
  validatePassword,
  getPasswordConditions,
  PASSWORD_CONDITIONS_TEXT,
} from '../../utils/password';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');

  const hasProfile = useSelector(selectHasProfile);
  const passwordConditions = getPasswordConditions(currentPassword);
  const userId = useSelector(selectUserId);
  const email = useSelector(selectEmail);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    if (!hasProfile) {
      const fetchProfile = async () => {
        setFetchingProfile(true);
        try {
          const userInfo = await getUserInfo();
          if (userInfo) {
            dispatch(setUser(userInfo));
          }
        } catch (err) {
          console.error('Failed to fetch profile:', err);
          navigate('/login');
        } finally {
          setFetchingProfile(false);
        }
      };
      fetchProfile();
    }
  }, [navigate, hasProfile, dispatch]);

  const handleResetPassword = async () => {
    try {
      setLoading(true);
      setError('');
      const { newPassword, confirmPassword } = form.getFieldsValue();

      if (newPassword !== confirmPassword) {
        setError(t('password_mismatch'));
        return;
      }

      const resolvedUserId =
        userId ||
        localStorage.getItem('userId') ||
        localStorage.getItem('userID');
      const resolvedEmail = email || localStorage.getItem('email') || '';

      if (!resolvedUserId || !resolvedEmail) {
        messageApi.error(t('reset_password_login_again'));
        navigate('/login', { replace: true });
        return;
      }

      const res = await resetPassword({
        userID: String(resolvedUserId),
        email: resolvedEmail,
        password: newPassword,
      });

      dispatch(setUser(res));
      navigate('/', { replace: true });
    } catch (err) {
      console.log(err);
      setError(err as string);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      handleResetPassword();
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Container>
      {contextHolder}
      <Content>
        <SVGLogoDark width={240} />
        <Welcome>Reset Password</Welcome>
        <FormCard>
          {!loading && error && <Alert message={error} type="error" showIcon />}
          {!loading && success && (
            <Alert
              message={t('password_reset_success')}
              type="success"
              showIcon
            />
          )}
          <p>
            <Typography.Text>
              It&apos;s your first time login, please set a new password to
              continue.
            </Typography.Text>
          </p>
          <Form
            form={form}
            layout="vertical"
            size="large"
            disabled={loading || fetchingProfile || success}
            onFinish={handleSubmit}
          >
            <Form.Item
              label={
                <span>
                  New Password{' '}
                  <Tooltip
                    title={
                      <div>
                        <p style={{ margin: 0 }}>
                          Password must contain at least:
                        </p>
                        <ul
                          style={{
                            margin: '4px 0 0',
                            paddingLeft: 16,
                            listStyle: 'none',
                          }}
                        >
                          {PASSWORD_CONDITIONS_TEXT.map((condition) => (
                            <li
                              key={condition.key}
                              style={{
                                fontWeight: passwordConditions[
                                  condition.key as keyof typeof passwordConditions
                                ]
                                  ? 600
                                  : 400,
                              }}
                            >
                              {condition.label}
                            </li>
                          ))}
                        </ul>
                      </div>
                    }
                  >
                    <InfoCircleOutlined style={{ color: '#999' }} />
                  </Tooltip>
                </span>
              }
              name="newPassword"
              rules={[{ validator: validatePassword }]}
            >
              <Input.Password
                placeholder={t('enter_new_password')}
                visibilityToggle={{
                  visible: passwordVisible,
                  onVisibleChange: setPasswordVisible,
                }}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </Form.Item>
            <Form.Item
              label={t('confirm_password')}
              name="confirmPassword"
              rules={[
                {
                  required: true,
                  message: 'Please confirm password!',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password
                placeholder={t('confirm_password')}
                visibilityToggle={{
                  visible: confirmPasswordVisible,
                  onVisibleChange: setConfirmPasswordVisible,
                }}
              />
            </Form.Item>
            <Button
              type="primary"
              size="large"
              block
              loading={loading || fetchingProfile}
              htmlType="submit"
            >
              Reset Password
            </Button>
          </Form>
        </FormCard>
      </Content>
    </Container>
  );
};

export default ResetPassword;
